import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMintActionHandlers } from 'state/islands/mint/hooks'

import { REFETCH_POLICY_FAST } from '../../../../config/RefetchPolicies'
import { useIslandContext } from '../hooks/useIslandContext'
import DepositView from './DepositView'
import { useStakingPosition } from './hooks/useStakingPositions'
import StakeView from './StakeView'
import * as S from './styleds'
import UnstakeView from './UnstakeView'
import WithdrawView from './WithdrawView'

const IslandManagementCard = () => {
  const [direction, setDirection] = useState<'deposit' | 'withdraw' | 'stake' | 'unstake'>('deposit')
  const { account } = useConnection()
  const context = useIslandContext()
  const token = useToken(context.islandId)
  const { onResetSingleSidedValues } = useMintActionHandlers(false)

  const {
    position,
    hasStakedBalance,
    isLoading: isLoadingStaking,
    kodiakFarm,
  } = useStakingPosition({
    stakingData: context.stakingData || { stakingType: 'none', farmAddress: undefined, vaultAddress: undefined },
  })

  useEffect(() => {
    onResetSingleSidedValues()
  }, [onResetSingleSidedValues, context.islandId])

  const lpBalance = useTokenBalance(account, token || undefined, REFETCH_POLICY_FAST)

  const render = useCallback(() => {
    switch (direction) {
      case 'deposit':
        return <DepositView {...context} />
      case 'withdraw':
        return <WithdrawView {...context} />
      case 'stake':
        return <StakeView {...context} />
      case 'unstake':
        return <UnstakeView {...context} />
      default:
        return <>Error Direction</>
    }
  }, [context, direction])

  // Memoize direction updates
  const handleDirectionChange = useCallback((newDirection: typeof direction) => {
    setDirection(newDirection)
  }, [])

  const canStake = !!lpBalance && lpBalance.greaterThan('0') && !context.isRewardVaultStakingDisabled

  useEffect(() => {
    if (!canStake && direction === 'stake') {
      handleDirectionChange('deposit')
    }

    if (!hasStakedBalance && direction === 'unstake') {
      handleDirectionChange('deposit')
    }
  }, [canStake, hasStakedBalance, direction, handleDirectionChange])

  // Get appropriate label for stake/unstake based on stakingType
  const getStakingLabel = useCallback(
    (baseLabel: string) => {
      if (context.stakingData?.stakingType === 'rewardVault') {
        return baseLabel === 'Stake' ? 'Stake' : 'Unstake'
      }
      return baseLabel
    },
    [context.stakingData?.stakingType]
  )

  const stakingSupported = useMemo(() => {
    if (!context.config || !context.stakingData) return false

    return (
      (context.config.supportsFarms && context.stakingData.stakingType === 'kodiakFarm') ||
      (context.config.supportsVaults && context.stakingData.stakingType === 'rewardVault')
    )
  }, [context.config, context.stakingData])

  const isLoading = isLoadingStaking

  if (!context.config || !context.stakingData) {
    return (
      <S.IslandManagementCard>
        <div style={{ padding: '20px', textAlign: 'center' }}>Loading pool configuration...</div>
      </S.IslandManagementCard>
    )
  }

  return (
    <S.IslandManagementCard>
      <S.DepositWithdrawSwitch>
        <S.DepositWithdrawOption
          onClick={() => {
            handleDirectionChange('deposit')
          }}
          active={direction === 'deposit'}
        >
          <>Deposit</>
        </S.DepositWithdrawOption>
        <S.DepositWithdrawOption
          onClick={() => {
            handleDirectionChange('withdraw')
          }}
          active={direction === 'withdraw'}
          tabIndex={-1}
        >
          <>Withdraw</>
        </S.DepositWithdrawOption>
        {stakingSupported && (
          <>
            {canStake && (
              <S.DepositWithdrawOption
                onClick={() => handleDirectionChange('stake')}
                active={direction === 'stake'}
                tabIndex={-1}
              >
                <>{getStakingLabel('Stake')}</>
              </S.DepositWithdrawOption>
            )}

            {hasStakedBalance && (
              <S.DepositWithdrawOption
                onClick={() => handleDirectionChange('unstake')}
                active={direction === 'unstake'}
                tabIndex={-1}
              >
                <>{getStakingLabel('Unstake')}</>
              </S.DepositWithdrawOption>
            )}
          </>
        )}
      </S.DepositWithdrawSwitch>
      {render()}
    </S.IslandManagementCard>
  )
}

export default IslandManagementCard
